/**
 * Page values — pure model for "page selection drives a property".
 *
 * A Pages container that also declares properties can give each page its own
 * value: the page carries a self-targeted state assignment
 * (`pages[i].states: [{ target: '$self', state }]`) and the container's
 * `Properties.states[state].values` holds the overrides. A page with no
 * override shows the declaration's base value. This is the shape the legacy
 * FollowActive compile emits (trace-viewer LegacyConstraints) — desks sit at
 * `{deckHeight}`, and the ESPN stage page sets `deckHeight: 0.597`.
 *
 * Pure functions over PagesData/PropertiesData; the action layer in
 * `page-values.ts` commits them.
 */

import type { PageData, PropertiesData, PropertyValue, StateDefinition } from 'trace-viewer/behaviors';
import { setAssignmentForTarget } from 'trace-viewer/editor';

/** The assignment target that names the container itself. */
export const SELF_TARGET = '$self';

/** The state page `pageIndex` assigns to the container itself, if any. */
export function pageSelfState(pages: readonly PageData[] | undefined, pageIndex: number): string | null {
    return pages?.[pageIndex]?.states?.find(a => a.target === SELF_TARGET)?.state ?? null;
}

/**
 * Whether a page other than `pageIndex` also assigns `stateName` to the
 * container. Page duplication copies assignments verbatim, so two pages can
 * point at one state — the duplicate deliberately looks like the original
 * until edited. Writing into that state would edit both pages.
 */
export function selfStateSharedWithAnotherPage(
    pages: readonly PageData[] | undefined,
    pageIndex: number,
    stateName: string,
): boolean {
    return (pages ?? []).some((p, i) => i !== pageIndex
        && p?.states?.some(a => a.target === SELF_TARGET && a.state === stateName));
}

/** The override page `pageIndex` holds for `prop`, or undefined (= base value). */
export function readPageOverride(
    props: PropertiesData,
    pages: readonly PageData[] | undefined,
    pageIndex: number,
    prop: string,
): PropertyValue | undefined {
    const state = pageSelfState(pages, pageIndex);
    return state ? props.states?.[state]?.values?.[prop] : undefined;
}

export interface PageValuesWrite {
    props: PropertiesData;
    pages: PageData[] | undefined;
    /** The page's self state after the write (null when it was removed). */
    stateName: string | null;
}

/**
 * Set `prop` to `value` on page `pageIndex`.
 *
 * The page's self state is reused when it has one; otherwise a state named
 * after the page is minted (the compile's convention — the state strip shows
 * the page the author knows). A same-named state that another page already
 * uses is not shared: values would leak across pages, so a suffixed name is
 * minted instead.
 */
export function setPageOverride(
    props: PropertiesData,
    pages: readonly PageData[] | undefined,
    pageIndex: number,
    pageName: string,
    pageCount: number,
    prop: string,
    value: PropertyValue,
): PageValuesWrite {
    const states = { ...(props.states ?? {}) };
    const assigned = pageSelfState(pages, pageIndex);
    // Copy-on-write: a state another page also points at is forked — a new
    // state named after this page, seeded from the shared one so unedited
    // values keep the inherited look — and this page is retargeted to it.
    const shared = !!assigned && selfStateSharedWithAnotherPage(pages, pageIndex, assigned);
    const stateName = assigned && !shared ? assigned : mintStateName(pageName, pages, states);
    const current: StateDefinition = states[stateName]
        ?? (shared && assigned && states[assigned] ? structuredClone(states[assigned]) : { values: {}, transition: { duration: 0 } });
    states[stateName] = { ...current, values: { ...current.values, [prop]: value } };
    return {
        props: { ...props, states },
        pages: setAssignmentForTarget(pages, pageIndex, SELF_TARGET, stateName, pageCount),
        stateName,
    };
}

/**
 * Remove page `pageIndex`'s override for `prop`. A self state left with
 * nothing in it is deleted along with its assignment — an empty state on the
 * strip would be noise.
 */
export function clearPageOverride(
    props: PropertiesData,
    pages: readonly PageData[] | undefined,
    pageIndex: number,
    pageName: string,
    pageCount: number,
    prop: string,
): PageValuesWrite {
    const assigned = pageSelfState(pages, pageIndex);
    const existing = assigned ? props.states?.[assigned] : undefined;
    if (!assigned || !existing) return { props, pages: pages ? [...pages] : undefined, stateName: assigned };

    const { [prop]: _removed, ...values } = existing.values ?? {};
    const remaining: StateDefinition = { ...existing, values };
    const shared = selfStateSharedWithAnotherPage(pages, pageIndex, assigned);

    if (!stateHasContent(remaining)) {
        // Nothing left for this page. A state other pages still use stays;
        // only this page's assignment goes.
        const states = { ...(props.states ?? {}) };
        if (!shared) delete states[assigned];
        return {
            props: { ...props, states },
            pages: setAssignmentForTarget(pages, pageIndex, SELF_TARGET, null, pageCount),
            stateName: null,
        };
    }
    if (shared) {
        // Copy-on-write, as in setPageOverride: the other page keeps the
        // shared state untouched; this page gets its own, minus the value.
        const states = { ...(props.states ?? {}) };
        const forked = mintStateName(pageName, pages, states);
        states[forked] = remaining;
        return {
            props: { ...props, states },
            pages: setAssignmentForTarget(pages, pageIndex, SELF_TARGET, forked, pageCount),
            stateName: forked,
        };
    }
    return {
        props: { ...props, states: { ...props.states, [assigned]: remaining } },
        pages: pages ? [...pages] : undefined,
        stateName: assigned,
    };
}


/** Whether a state still overrides anything (values, pose, or visibility). */
export function stateHasContent(state: StateDefinition): boolean {
    return Object.keys(state.values ?? {}).length > 0
        || !!state.transform
        || state.active !== undefined
        || !!state.enterFrom;
}

/**
 * A state name for the page: its name, suffixed if that name is already
 * taken — by another page's self assignment, or by any existing state (an
 * orphan left by a renamed page, say). A fresh override never merges into a
 * state it did not create; stale values must not leak into the new page.
 */
function mintStateName(
    pageName: string,
    pages: readonly PageData[] | undefined,
    states: Record<string, StateDefinition>,
): string {
    const base = pageName.replace(/^:+/, '').trim() || 'page';
    const taken = (name: string): boolean =>
        name in states
        || (pages ?? []).some(p => p?.states?.some(a => a.target === SELF_TARGET && a.state === name));
    if (!taken(base)) return base;
    let n = 2;
    while (taken(`${base} ${n}`)) n++;
    return `${base} ${n}`;
}
